import React from 'react';
import 'twin.macro';
import { UserLocale } from '@teetsh/app/src/types/user';
import { Field, useFormState } from 'react-final-form';
import { AppelsStatsPDFSettings } from '@teetsh/app/src/containers/RegistreAppel/Print/types';
import { useDownloadAppelsStats } from '@teetsh/app/src/queries/appels/useDownloadAppelsStats';
import { useTranslation } from 'react-i18next';
import { SidebarWithSections } from 'components/SubSidebar/SidebarWithSections';
import Button from 'components/Buttons/Button';
import {
  ArrowDownCircleIcon,
  PrinterIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import FormGroup from 'components/Form/FormGroup';
import { FontsCoupleSelector } from 'components/Form/FontsCoupleSelector';
import { FontSizeSelector } from 'components/FontSizeSelector';
import { ColorSelector } from 'components/Form/ColorSelector';
import { BgImageSelectorWithImages } from '@teetsh/app/src/components/Form/BgImageSelector/BgImageSelectorWithImages';
import { MarginsFieldWrapper } from 'components/Margins';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';

interface Props {
  from: Date;
  to: Date;
  lng: UserLocale;
  tz: string;
  limitedBgSet: boolean;
  eleveId?: string;
}

export function Sidebar(props: Props) {
  const { from, to, eleveId, lng, tz, limitedBgSet } = props;
  const { values } = useFormState<AppelsStatsPDFSettings>();
  const { download, isDownloading } = useDownloadAppelsStats();
  const onDownload = () =>
    download({
      from,
      to,
      eleveId,
      lng,
      tz,
      settings: values,
    });
  const { t } = useTranslation(['common', 'registreAppel']);

  return (
    <SidebarWithSections
      collapsedComponent={
        <SidebarWithSections.CollapsedButton
          icon={ArrowDownCircleIcon}
          title={t('common:buttons.downloadPdf')}
          loading={isDownloading}
          onClick={() => onDownload()}
        />
      }
    >
      <div tw="p-4">
        <Button
          fill
          large
          intent="primary"
          icon={ArrowDownCircleIcon}
          loading={isDownloading}
          onClick={() => onDownload()}
        >
          {t('common:buttons.downloadPdf')}
        </Button>
      </div>

      <SidebarWithSections.Section
        icon={SwatchIcon}
        title={t('common:pdf.appearance')}
      >
        <FormGroup label={t('common:pdf.fonts')}>
          <Field
            name="font"
            component={({ input: { onChange, value } }) => (
              <FontsCoupleSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.fontSize')}>
          <Field
            name="fontSize"
            label={t('common:pdf.fontSize')}
            render={({ input: { onChange, value } }) => (
              <FontSizeSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgColor')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector light value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgImages')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <BgImageSelectorWithImages
                value={value}
                onChange={onChange}
                limitedBgSet={limitedBgSet}
              />
            )}
          />
        </FormGroup>
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={PrinterIcon}
        title={t('common:pdf.layout')}
      >
        <FormGroup label={t('common:pdf.margins')}>
          <MarginsFieldWrapper />
        </FormGroup>
        <Field
          name="withPagination"
          type="checkbox"
          render={({ input }) => (
            <Checkbox
              id="withPagination"
              label={t('common:pdf.withPagination')}
              description={t('common:pdf.onlyVisibleOnPdf')}
              {...input}
            />
          )}
        />
      </SidebarWithSections.Section>
    </SidebarWithSections>
  );
}
